import React, { useEffect, useRef, useState } from "react";
import { useAudio } from "../providers/AudioProvider";
import { makeMediaRequest } from "../services/ChatService";

interface MediaDisplayProps {
  mediaUrl: string | null; // url of media blob
  onVideoEnd?: () => void; // callback when video ends
  mediaId?: string; // pass the messageId from MessageItem
  originalMediaPath?: string; // original S3 path for URL refresh
  onMediaUrlUpdate?: (newUrl: string) => void; // callback when URL is refreshed
}

export const MediaDisplay = ({
  mediaUrl,
  onVideoEnd,
  mediaId,
  originalMediaPath,
  onMediaUrlUpdate,
}: MediaDisplayProps) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState(mediaUrl);
  const [isRefreshingUrl, setIsRefreshingUrl] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>(); // timeout for the controls to hide
  const audio = useAudio();
  const { onVideoEnd: onVideoEndProvider, fullscreenVideoMessageId } = audio;

  const isFullscreen = fullscreenVideoMessageId === mediaId;

  const { url, isVideo } = {
    url: currentVideoUrl,
    isVideo: currentVideoUrl?.includes(".mp4"),
  };

  if (!mediaId) return null;

  const videoId = mediaId.startsWith("vid-") ? mediaId : `vid-${mediaId}`;

  // Function to refresh the video URL
  const refreshVideoUrl = async () => {
    if (!originalMediaPath || isRefreshingUrl || retryCount >= 3) return;

    setIsRefreshingUrl(true);
    setRetryCount((prev) => prev + 1);

    try {
      const response = await makeMediaRequest({
        query_id: mediaId || "video-refresh",
        media_paths: [originalMediaPath],
      });

      if (response && typeof response === "object" && "url" in response) {
        const newUrl = response.url;
        setCurrentVideoUrl(newUrl);
        setRetryCount(0);

        // Notify parent component about the URL update
        onMediaUrlUpdate?.(newUrl);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error(`[MediaDisplay] Failed to refresh video URL:`, error);
      alert(
        "Video Failed to Load: Please refresh the page and try again later."
      );
    } finally {
      setIsRefreshingUrl(false);
      setTimeout(() => audio.play(videoId), 200);
    }
  };

  useEffect(() => {
    if (mediaUrl) {
      setCurrentVideoUrl(mediaUrl);
    }
  }, [mediaUrl]);

  // Subscribe to media state store
  const [mediaState, setMediaState] = useState(() =>
    audio.mediaState.select(videoId)
  );

  useEffect(() => {
    const unsubscribe = audio.mediaState.subscribe(() => {
      setMediaState(audio.mediaState.select(videoId));
    });
    return unsubscribe;
  }, [videoId, audio.mediaState]);

  // Determine if video is playing from centralized state
  const isPlaying = mediaState.isCurrent && mediaState.status === "playing";

  // Register / unregister with MediaContext
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    audio.registerVideoSource(mediaId, videoRef.current);
    return () => void audio.unregisterVideoSource(mediaId);
  }, [mediaId, isVideo]);

  // Handle video events
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;

    const video = videoRef.current;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsVideoReady(true);
    };

    const handleEnded = () => {
      setCurrentTime(0);
      // Reset video to beginning when it ends so it can be replayed
      video.currentTime = 0;
      onVideoEndProvider(mediaId); // tell provider
      onVideoEnd?.(); // if you want to keep the prop as well
    };

    const onVideoVolumeChangeEvent = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    const handleVideoError = (event: Event) => {
      const target = event.target as HTMLVideoElement;
      const error = target.error;

      if (error) {
        if (
          error.code === MediaError.MEDIA_ERR_NETWORK ||
          error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
          target.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
        ) {
          refreshVideoUrl();
        }
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("volumechange", onVideoVolumeChangeEvent);
    video.addEventListener("error", handleVideoError);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("volumechange", onVideoVolumeChangeEvent);
      video.removeEventListener("error", handleVideoError);
    };
  }, [
    mediaId,
    isVideo,
    audio,
    onVideoEnd,
    onVideoEndProvider,
    refreshVideoUrl,
  ]);

  // Auto-hide controls
  useEffect(() => {
    if (!isVideo) return;

    const resetControlsTimeout = () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      setShowControls(true);
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };

    resetControlsTimeout();

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isVideo, isPlaying]);

  // Prevent native fullscreen on mobile devices
  useEffect(() => {
    if (!isVideo) return;

    const handleFullscreenChange = () => {
      // Check if native fullscreen is active
      const isNativeFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

      if (isNativeFullscreen) {
        // Exit native fullscreen immediately
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
      }
    };

    // Listen for fullscreen change events
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [isVideo]);

  const handlePlayPause = async () => {
    if (!videoRef.current) {
      return;
    }

    if (isPlaying) {
      // Use MediaContext pause method for proper coordination
      audio.pause(videoId);
    } else {
      // Play video without entering fullscreen
      audio.play(videoId);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) {
      return;
    }
    const newTime = parseFloat(e.target.value);

    try {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    } catch (error) {
      console.error("[MediaDisplay] Seek failed:", error);
      if (error instanceof Error) {
        console.error("[MediaDisplay] Seek error details:", {
          name: error.name,
          message: error.message,
        });
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) {
      return;
    }
    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;
    setVolume(newVolume);
  };

  const handleMuteToggle = () => {
    if (!videoRef.current) {
      return;
    }
    const newMuteState = !isMuted;
    videoRef.current.muted = newMuteState;
    setIsMuted(newMuteState);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    // Prevent default browser behavior and stop event bubbling
    // Without these:
    // - e.preventDefault(): Browser might try to show native video controls or context menu
    // - e.stopPropagation(): Click would bubble up to parent MessageItem's onClick handler without this
    e.preventDefault();
    e.stopPropagation();

    if (isVideo) {
      handlePlayPause();
    }
  };

  const handleMouseMove = () => {
    if (isVideo) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      setShowControls(true);
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    }
  };

  if (!url) return null;

  return (
    <>
      {/* Single video element that swaps between inline and fullscreen modes */}
      {isVideo ? (
        <div
          className={`${isFullscreen ? "fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" : "mt-2 sm:mt-3 relative"}`}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
            if (controlsTimeoutRef.current) {
              clearTimeout(controlsTimeoutRef.current);
            }
            if (isPlaying) {
              controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
              }, 3000);
            }
          }}
        >
          {/* Video container */}
          <div
            className={`relative ${isFullscreen ? "w-auto h-auto max-w-[90vw] max-h-[90vh]" : "w-full max-w-[300px] sm:max-w-[400px] md:max-w-[500px] lg:max-w-[600px] xl:max-w-[700px] h-[200px] sm:h-[250px] md:h-[300px] lg:h-[350px] xl:h-[400px] rounded-lg cursor-pointer"}`}
          >
            <video
              ref={videoRef}
              crossOrigin="anonymous"
              src={url}
              className={`rounded-lg object-contain [&::-webkit-media-controls]:hidden [&::-webkit-media-controls-panel]:hidden [&::-webkit-media-controls-play-button]:hidden [&::-webkit-media-controls-start-playback-button]:hidden [&::-webkit-media-controls-timeline]:hidden [&::-webkit-media-controls-current-time-display]:hidden [&::-webkit-media-controls-time-remaining-display]:hidden [&::-webkit-media-controls-time-control]:hidden [&::-webkit-media-controls-volume-slider]:hidden [&::-webkit-media-controls-volume-slider-container]:hidden [&::-webkit-media-controls-volume-control]:hidden [&::-webkit-media-controls-mute-button]:hidden [&::-webkit-media-controls-toggle-closed-captions-button]:hidden [&::-webkit-media-controls-fullscreen-button]:hidden [&::-webkit-media-controls-picture-in-picture-button]:hidden ${isFullscreen ? "max-w-[90vw] max-h-[90vh]" : "w-full h-full"}`}
              data-trust-trigger="media-display"
              onClick={handleVideoClick}
              onTouchStart={(e) => e.preventDefault()}
              onTouchMove={(e) => e.preventDefault()}
              onTouchEnd={(e) => e.preventDefault()}
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              playsInline
              webkit-playsinline="true"
              x5-playsinline="true"
              x5-video-player-type="h5"
              x5-video-player-fullscreen="false"
              x5-video-orientation="portraint"
              style={{
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
                userSelect: "none",
              }}
            />

            {/* Loading indicator for URL refresh */}
            {isRefreshingUrl && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                <div className="text-white text-center  bg-transparent">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2 bg-transparent"></div>
                  <div className="text-sm  bg-transparent">
                    Refreshing video...
                  </div>
                </div>
              </div>
            )}

            {/* Custom Controls Overlay - show in both inline and fullscreen modes */}
            {isVideoReady && !isRefreshingUrl && (
              <div
                className={`absolute inset-0 bg-black bg-opacity-0 transition-opacity duration-300 ${showControls ? "hover:bg-opacity-20" : ""}`}
              >
                {/* Play/Pause Button */}
                <button
                  onClick={handlePlayPause}
                  data-trust-trigger="media-display"
                  className={`bg-transparent absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-16 h-16 flex items-center justify-center hover:bg-opacity-70 transition-all ${showControls ? "opacity-100" : "opacity-0"}`}
                >
                  {isPlaying ? (
                    <svg
                      className="w-8 h-8"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg
                      className="w-8 h-8 ml-1"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>

                {/* Controls Bar */}
                <div
                  className={`bg-transparent absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent p-4 transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0"}`}
                >
                  {/* Progress Bar */}
                  <div className="mb-2 bg-transparent">
                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onChange={handleSeek}
                      className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-none"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / (duration || 1)) * 100}%, #6b7280 ${(currentTime / (duration || 1)) * 100}%, #6b7280 100%)`,
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-white text-sm bg-transparent">
                    <div className="flex items-center space-x-4">
                      {/* Play/Pause Button */}
                      <button
                        onClick={handlePlayPause}
                        data-trust-trigger="media-display"
                        className="text-white hover:text-gray-300"
                      >
                        {isPlaying ? (
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>

                      {/* Time Display */}
                      <span>
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>

                      {/* Volume Controls */}
                      <div className="flex items-center space-x-2 bg-transparent">
                        <button
                          onClick={handleMuteToggle}
                          data-trust-trigger="media-display"
                          className="text-white hover:text-gray-300"
                        >
                          {isMuted || volume === 0 ? (
                            <svg
                              className="w-5 h-5"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                            </svg>
                          ) : volume < 0.5 ? (
                            <svg
                              className="w-5 h-5"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                            </svg>
                          ) : (
                            <svg
                              className="w-5 h-5"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                            </svg>
                          )}
                        </button>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={volume}
                          onChange={handleVolumeChange}
                          className="w-16 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-none"
                        />
                      </div>
                    </div>

                    {/* Fullscreen Button */}
                    <button
                      onClick={() =>
                        audio.setFullscreenVideoMessageId(
                          isFullscreen ? null : mediaId
                        )
                      }
                      data-trust-trigger="media-display"
                      className="text-white hover:text-gray-300 bg-transparent"
                    >
                      {isFullscreen ? (
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Image handling
        <div className="mt-2 sm:mt-3 relative w-full max-w-[300px] sm:max-w-[400px] md:max-w-[500px] lg:max-w-[600px] xl:max-w-[700px] h-[200px] sm:h-[250px] md:h-[300px] lg:h-[350px] xl:h-[400px] rounded-lg cursor-pointer">
          <img
            src={url}
            alt="Media"
            className="w-full h-full rounded-lg object-contain"
            onClick={() => audio.setFullscreenVideoMessageId(mediaId)}
            data-trust-trigger="media-display"
          />

          {/* Fullscreen image modal */}
          {isFullscreen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
              onClick={() => audio.setFullscreenVideoMessageId(null)}
            >
              <div className="relative max-w-[90vw] max-h-[90vh]">
                <button
                  onClick={() => audio.setFullscreenVideoMessageId(null)}
                  data-trust-trigger="media-display"
                  className="absolute top-4 right-4 text-white text-2xl font-bold bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75 z-10"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
                <img
                  src={url}
                  alt="Media"
                  className="max-w-[90vw] max-h-[90vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
